import { Formik } from 'formik';
import { useEffect, useState } from 'react';

import { CloseButton } from '@components/ActionButtons/CloseButton';
import { Box } from '@components/Box';
import {
  FormButtonsContainer,
  FormSectionContainer,
} from '@components/Container';
import {
  CancelButton,
  FormikInput,
  FormikSelect,
  FormikTextArea,
  SubmitButton,
} from '@components/Form';
import { Space } from '@components/Space';
import { useNotification } from '@hooks/useNotification';
import { CategoryDefaultValue, CategoryValidation } from '@models/Category';
import { FormikSelectOption } from '@models/common/FormikSelectOption';
import { CategoryService } from '@services/Category';

interface Props {
  onClose?: (refreshGrid?: boolean, newId?: string) => void;
}

export const CategoryCreate: React.FC<Props> = ({ onClose }) => {
  const [parentCategories, setParentCategories] = useState<
    FormikSelectOption[]
  >([]);
  const { showError, showSuccess } = useNotification();

  const loadParentCategories = async () => {
    const result = await CategoryService.getParentCategorySelectOptions(0);

    if (result.isSuccess && 'data' in result) {
      const parentCategorySelections = [{ value: '0', text: '' }].concat(
        result.data.map((row) => ({
          value: row.value,
          text: row.text,
        }))
      );
      setParentCategories(parentCategorySelections);
    } else {
      console.error(
        'Error: Failed to get parent categories.',
        result.errorMessages
      );
      showError(`${result.errorMessages}`);
    }
  };

  useEffect(() => {
    loadParentCategories();
  }, []);

  return (
    <>
      <CloseButton onClick={onClose} />

      <Box size="medium">
        <Formik
          initialValues={CategoryDefaultValue}
          validationSchema={CategoryValidation}
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={(values, actions) => {
            if (values.parentId == 0) {
              values.parentId = null;
            }

            CategoryService.create(values)
              .then((result) => {
                if (result.isSuccess && 'data' in result) {
                  showSuccess('Successfully created a record.');

                  const newId = result.data.id.toString();
                  onClose && onClose(true, newId);
                } else {
                  console.error(
                    'Error: Failed to create a record.',
                    result.errorMessages
                  );
                  showError(`${result.errorMessages}`);
                }
              })
              .finally(() => {
                actions.setSubmitting(false);
              });
          }}
        >
          {(formikProps) => {
            return (
              <>
                <form method="POST" onSubmit={formikProps.handleSubmit}>
                  <FormSectionContainer>
                    <FormikInput
                      label="Category Name"
                      name="name"
                      autoComplete="category-name"
                    />

                    <FormikSelect
                      label="Parent"
                      name="parentId"
                      selection={parentCategories}
                    />
                    <FormikTextArea label="Description" name="description" />
                    <Space />
                  </FormSectionContainer>
                  <FormButtonsContainer>
                    <CancelButton onClick={() => onClose && onClose(false)} />

                    <SubmitButton
                      label="Save"
                      disabled={
                        formikProps.isSubmitting || !formikProps.isValid
                      }
                    />
                  </FormButtonsContainer>
                </form>
              </>
            );
          }}
        </Formik>
      </Box>
    </>
  );
};
